import type { Platform, EnvironmentInfo, PlatformCapabilities, PlatformConfig, AsyncStorage } from '../core';
import { ErrorHandler, PlatformEnum } from '../utils';

/**
 * 基础适配器抽象类
 * 提供通用的实现和错误处理机制
 */
export abstract class BaseAdapter implements Platform {
  protected errorHandler: ErrorHandler;
  protected config: PlatformConfig;

  protected constructor(config: Partial<PlatformConfig> = {}) {
    this.errorHandler = ErrorHandler.getInstance();
    this.config = {
      enableErrorHandling: true,
      enableFallback: true,
      enableDebug: false,
      defaultViewportSize: { width: 375, height: 667 },
      ...config,
    };
  }

  // ========== 抽象属性 ==========
  abstract readonly platformType: PlatformEnum;
  abstract readonly env: EnvironmentInfo;
  abstract readonly capabilities: PlatformCapabilities;

  // ========== 环境信息 ==========
  get isMobile(): boolean {
    return this.env.isMobile;
  }

  get isTouch(): boolean {
    return this.env.isTouch;
  }

  /**
   * 判断当前环境是否为Liteapp
   * @returns 是否为Liteapp环境
   */
  isLiteapp(): boolean {
    return this.platformType === PlatformEnum.LiteApp;
  }


  // ========== 存储系统 ==========
  get localStorage(): AsyncStorage {
    return this.safeCall('localStorage', () => this.getDefaultLocalStorage());
  }
  getCookie(name: string): string {
    return this.safeCall('cookie', () => this.getDefaultCookie(name));
  }
  setCookie(options: { name: string; value: string; days?: number | undefined; path?: string | undefined; domain?: string | undefined; secure?: boolean | undefined; }) {
    return this.safeCall('cookie', () => this.setDefaultCookie(options));
  }
  // ========== 网络请求 ==========
  // fetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  //   return this.safeCall('fetch', () => this.doDefaultFetch(input, init));
  // }
  // ========== 浏览器对象 ==========
  get navigator(): Navigator {
    return this.safeCall('navigator', () => this.getDefaultNavigator());
  }
  get location() {
    return this.safeCall('location', () => this.getDefaultLocation());
  }

  /**
   * 检查是否支持某个特性
   * @param feature
   */
  supports(feature: string): boolean {
    return this.capabilities[feature as keyof PlatformCapabilities] ?? false;
  }

  /**
   * 安全调用API
   * @param apiName
   * @param fn
   * @param fallback
   */
  safeCall<T>(
    apiName: string,
    fn: () => T,
    fallback?: () => T,
  ): T {
    if (!this.config.enableErrorHandling) {
      return fn();
    }

    try {
      return fn();
    } catch (error) {
      if (this.config.enableFallback && fallback) {
        this.errorHandler.handleFallbackUsed(apiName, this.platformType, 'fallback');
        try {
          return fallback();
        } catch (fallbackError) {
          return this.errorHandler.handleApiFailed(apiName, this.platformType, fallbackError as Error);
        }
      }
      return this.errorHandler.handleApiFailed(apiName, this.platformType, error as Error);
    }
  }

  protected abstract getDefaultLocalStorage(): AsyncStorage;
  // protected abstract doDefaultFetch(input: RequestInfo, init?: RequestInit): Promise<Response>;
  protected abstract getDefaultNavigator(): Navigator;
  protected abstract getDefaultCookie(name: string): string;
  protected abstract setDefaultCookie(options: { name: string; value: string; days?: number | undefined; path?: string | undefined; domain?: string | undefined; secure?: boolean | undefined; }): void;
  protected abstract getDefaultLocation(): Location;
  protected abstract getDefaultDocument(): Document;

  /** 获取全局数据配置（只读） */
  public abstract getGlobalDataConfig(): Record<string, unknown>;
}
